import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Helmet } from "react-helmet-async";
const ManufacturingSolutions = () => {
  const phrases = [
    "Smart Factory Automation",
    "IIoT Predictive Analytics",
    "MES Digital Thread Sync"
  ];

  const cardData = [
    { type: 'mfg-core', content: '🏭' },
    { type: 'safety-amber', content: '📊' },
    { type: 'iiot-stack', content: '⚙️' },
    { type: 'automation-spark', content: '⚡' }
  ];

  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [cardIndex, setCardIndex] = useState(0);

  useEffect(() => {
    const currentPhrase = phrases[currentPhraseIndex];
    let timer;

    const handleTyping = () => {
      if (!isDeleting) {
        setDisplayedText(currentPhrase.substring(0, displayedText.length + 1));
        if (displayedText === currentPhrase) {
          timer = setTimeout(() => setIsDeleting(true), 1600);
          return;
        }
      } else {
        setDisplayedText(currentPhrase.substring(0, displayedText.length - 1));
        if (displayedText === '') {
          setIsDeleting(false);
          setCurrentPhraseIndex((prev) => (prev + 1) % phrases.length);
          return;
        }
      }

      const speed = isDeleting ? 30 : 80;
      timer = setTimeout(handleTyping, speed);
    };

    timer = setTimeout(handleTyping, 100);
    return () => clearTimeout(timer);
  }, [displayedText, isDeleting, currentPhraseIndex]);

  useEffect(() => {
    const cardTimer = setInterval(() => {
      setCardIndex((prev) => (prev + 1) % cardData.length);
    }, 1100);
    return () => clearInterval(cardTimer);
  }, []);

  // Intersection Observer for Scroll Animations
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.15
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-visible');
        }
      });
    }, observerOptions);

    const animatedElements = document.querySelectorAll(
      '.scroll-anim-fade-in, .scroll-anim-fade-in-up, .scroll-anim-fade-in-left, .scroll-anim-fade-in-right, .scroll-anim-flip, .scroll-anim-zoom-in'
    );

    animatedElements.forEach((el) => observer.observe(el));

    return () => {
      animatedElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  // Content Data
  const coreServices = [
    { icon: "🏭", title: "Manufacturing Execution System (MES) Setup", desc: "Integrate production floor execution layers directly with corporate ERP networks to track raw material consumption, plant routing, and work-in-progress statuses instantly." },
    { icon: "⚡", title: "IIoT Smart Asset Architecture", desc: "Connect industrial machine PLCs and sensor arrays securely using OPC UA and MQTT protocols to capture microsecond pressure, vibration, and heat diagnostics." },
    { icon: "⚙️", title: "AI Predictive Maintenance Systems", desc: "Deploy automated machine learning pipelines to detect anomalous telemetry trends, forecast structural component decay, and prevent costly unplanned outages." },
    { icon: "📊", title: "Overall Equipment Effectiveness (OEE) Lenses", desc: "Ingest and process shop-floor runtime logs into clean executive metrics tracking overall plant performance, availability, and production quality margins." },
    { icon: "🔗", title: "Supply Chain & Logistics Synchronization", desc: "Unify supplier lead schedules, multi-location warehouse operations, automated bill of materials (BOM), and material requirement planning (MRP) frameworks." },
    { icon: "🛡️", title: "OT Cybersecurity & Zero-Trust Architecture", desc: "Isolate operational tech environments from public networks using strict network segmentation, secure edge firewalls, and air-gapped data brokers." },
    { icon: "🔄", title: "Computerized Maintenance Management (CMMS)", desc: "Automate corrective maintenance tickets, spare parts inventory alerts, and routine equipment calibration matrices based on runtime hours." }
  ];

  const advancedServices = [
    { icon: "🔍", title: "Computer Vision Automated Quality Control", desc: "Deploy high-speed edge camera networks running convolutional neural patterns to identify physical component micro-defects right on the assembly line." },
    { icon: "💼", title: "Digital Twin Multi-Physics Modeling", desc: "Construct functional virtual mirrors of complete production assembly structures to simulate asset configurations prior to executing factory changes." },
    { icon: "🗃️", title: "Energy Management & Sustainability Tuning", desc: "Track power footprint spikes across high-capacity machinery blocks to optimize line run intervals and reduce base utility operational costs." },
    { icon: "⚖️", title: "Automated Compliance & Track-and-Trace", desc: "Architect serialization frameworks recording immutable production histories for components, ensuring strict safety and regulatory compliance." },
    { icon: "🚨", title: "Warehouse Robotics & AGV Orchestration", desc: "Interface modern automated guided vehicles and smart item picking infrastructure directly into inventory staging and dispatch bays." },
    { icon: "📝", title: "PLM Design System Integrations", desc: "Bridge Product Lifecycle Management designs directly into assembly frameworks to eliminate engineering revision lag variations." },
    { icon: "🌐", title: "Edge Compute Gateway Implementations", desc: "Deploy highly ruggedized compute boxes directly on shop floor panels to clean, compress, and analyze factory metrics locally before cloud uploads." },
    { icon: "🧪", title: "Advanced Batch & Recipe Management", desc: "Implement strict, formula-driven sequencing logic with zero-fault tolerances across heavy chemical, food, or pharmaceutical processing lines." },
    { icon: "📦", title: "Dynamic Demand & Capacity Forecasting", desc: "Synthesize field historical shipment velocity markers with real-time sales pipelines to adjust active production plant volume schedules dynamically." }
  ];

  const technologies = {
    "Industrial Systems (MES/OT)": ["Siemens Opcenter / Rockwell MES", "Ignition SCADA / Aveva Wonderware", "OPC UA / MQTT Telemetry Protocols", "Allen-Bradley / Modbus PLC Networks"],
    "Data & IIoT Analytics": ["AWS IoT Core / Azure IoT Hub", "Time-Series Databases (InfluxDB)", "Apache Kafka Streaming Fabrics", "Power BI Industrial OEE Dashboards"],
    "Enterprise Automation": ["SAP Digital Supply Chain Nodes", "Oracle NetSuite Supply Chain Modules", "Microsoft Dynamics 365 Asset Sync", "Custom PLM / CAD Pipeline Connectors"],
    "Edge & AI Infrastructure": ["NVIDIA Jetson Edge Vision Blocks", "TensorFlow Automated Defect Models", "Ruggedized Industrial Edge Gateways", "AWS Bedrock Predictive Maintenance RAG"],
    "Operational Management": ["Fiix CMMS / UpKeep Asset Trackers", "Automated MRP II Engine Modules", "Dynamic BOM Configuration Systems", "ISO 9001 Digital Quality Control Gates"]
  };

  const processSteps = [
    { num: "01", title: "Shop Floor & Architecture Auditing", desc: "Mapping industrial machine interface nodes, identifying air-gapped components, and assessing historical system operational lag." },
    { num: "02", title: "Edge Network & PLC Blueprinting", desc: "Structuring secure VLAN partitions, specifying telemetry tag directories, and mapping message payload structures." },
    { num: "03", title: "SCADA & Middleware Synthesis", desc: "Configuring ingestion networks, building data normalization structures, and writing initial automation routing logic." },
    { num: "04", title: "ERP Integration & Data Mapping", desc: "Linking shop-floor execution streams directly to enterprise accounting, inventory layers, and vendor lines." },
    { num: "05", title: "Model Testing & Cyber Validation", desc: "Verifying anomaly detection accuracy, testing localized edge failovers, and checking zero-trust network block limits." },
    { num: "06", title: "Live System Cutover & Line Sync", desc: "Activating real-time data ingestion matrices across active assembly sectors with zero interruption to active production cycles." },
    { num: "07", title: "Continuous Optimization Loops", desc: "Reviewing plant OEE metric performance, reducing telemetry traffic bottlenecks, and updating anomaly pattern models regularly." }
  ];

  const industries = [
    "High-Volume Automotive & Component Assembly Plants", "Cross-Border Specialized Pharmaceutical & Chemical Processing", "Aerospace, Defense & Heavy Industrial Machinery",
    "High-Density Consumer Packaged Goods & Electronics", "Food, Beverage & Perishable Cold Chain Production", "Heavy Metal Refining, Smelting & Structural Steel Milling",
    "High-Scale Semiconductor & Cleanroom Microchip Fab Nodes", "Energy Generation, Utilities & Infrastructure Networks", "Advanced Building Materials & Construction Component Fab",
    "Paper, Pulp & Automated High-Volume Packaging Plants", "Textile Manufacturing, Industrial Garments & Operations", "Medical Device Fabrication & Sterile Equipment Assembly"
  ];

  const designBenefits = [
    "Zero Material Reconciliation Gaps Across Assembly Lines", "Up to 35% Reduction in Unplanned Equipment Maintenance Outages", "100% Secure, Air-Gapped Industrial Network Topologies",
    "Guaranteed 99.95% Factory Floor Data Network Availability", "Drastic Reduction in Manual Shift Logging and Tracking Tasks", "Highly Scalable IIoT Machine Telemetry Storage Ingestion",
    "Seamless Shop-Floor-to-Boardroom Enterprise ERP Integration", "Real-Time OEE & Plant Performance Ingestion Metrics"
  ];

  const whyChooseUs = [
    "Certified Industrial Automation Solutions & IIoT Systems Architects", "Proven Track Record Modernizing Heavy Industrial Manufacturing Floor Infrastructure", "Airtight Zero-Trust OT Boundary Security Governance Patterns",
    "Deep Competency in High-Throughput Time-Series Factory Data Optimization", "Platform-Agnostic PLC, SCADA, and MES Interfacing Capabilities", "Resilient High-Availability Local Edge Compute Ingestion Deployments",
    "End-to-End Factory Lifecycle Governance and System Transport Frameworks", "Transparent Engineering Goals & Verifiable Field Testing Metrics", "High-Availability Platform Integration Infrastructure SLAs",
    "Experienced Automation Engineers Specializing in ISO Quality System Matrices", "Iterative Proof-of-Concept Target Environment Testing Sprints", "100% Production Plant Operations Architecture Stability Assurances"
  ];

  return (
    <>

      <Helmet>
        {/* Primary SEO */}
        <title>
          Manufacturing Solutions | Smart Manufacturing & Industrial Automation | Affsoft IT Solutions Pvt. Ltd.
        </title>

        <meta
          name="title"
          content="Manufacturing Solutions | Smart Manufacturing & Industrial Automation | Affsoft IT Solutions Pvt. Ltd."
        />

        <meta
          name="description"
          content="Affsoft IT Solutions Pvt. Ltd. provides end-to-end Manufacturing Solutions, including ERP for manufacturing, production planning, inventory management, supply chain optimization, quality control, warehouse management, and industrial automation to improve operational efficiency."
        />

        <meta
          name="keywords"
          content="Manufacturing Solutions, Manufacturing ERP, Smart Manufacturing, Industrial Automation, Production Planning, Inventory Management, Supply Chain Management, Warehouse Management, Quality Control, Factory Management Software, Manufacturing Process Optimization, Digital Manufacturing, Enterprise Manufacturing Solutions, Industry 4.0, Manufacturing Software, Affsoft IT Solutions Pvt. Ltd."
        />

        <meta name="robots" content="index, follow" />
        <meta name="author" content="Affsoft IT Solutions Pvt. Ltd." />
        <meta name="language" content="English" />
        <meta name="theme-color" content="#0d5c53" />

        {/* Canonical */}
        <link
          rel="canonical"
          href="https://www.affsoft.com/manufacturing-solutions"
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta
          property="og:site_name"
          content="Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          property="og:title"
          content="Manufacturing Solutions | Smart Manufacturing Software | Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          property="og:description"
          content="Streamline production with Affsoft's Manufacturing Solutions. We offer manufacturing ERP, production planning, inventory management, supply chain optimization, quality management, warehouse solutions, and industrial automation."
        />
        <meta
          property="og:url"
          content="https://www.affsoft.com/manufacturing-solutions"
        />
        <meta
          property="og:image"
          content="https://www.affsoft.com/images/affsoft-logo.png"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Manufacturing Solutions | Smart Manufacturing & Industrial Automation | Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          name="twitter:description"
          content="Improve manufacturing efficiency with Affsoft IT Solutions. Our solutions include manufacturing ERP, production planning, inventory control, warehouse management, supply chain optimization, and Industry 4.0 technologies."
        />
        <meta
          name="twitter:image"
          content="https://www.affsoft.com/images/affsoft-logo.png"
        />
      </Helmet>
      <div className="manufacturing-services-page bg-light" style={{ fontFamily: '"Inter", sans-serif', overflowX: 'hidden' }}>

        {/* ================= HERO SECTION ================= */}
        <div
          className="hero-container container-fluid d-flex align-items-center justify-content-center position-relative overflow-hidden"
          style={{
            backgroundImage: "url('./images/home.jpg')",
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            minHeight: '85vh',
            backgroundColor: '#1a222d' // Deep Industrial Slate fallback
          }}
        >
          {/* Background Grid Lines */}
          <div className="grid-overlay position-absolute top-0 start-0 w-100 h-100" style={{ opacity: 0.12 }}>
            <div className="grid-line line-v-1"></div>
            <div className="grid-line line-v-2"></div>
            <div className="grid-line line-v-3"></div>
          </div>

          {/* Infinite Marquee Text */}
          <div className="marquee-wrapper select-none position-absolute w-100 scroll-anim-fade-in" style={{ top: '15%', opacity: 0.05 }}>
          </div>

          <div className="marquee-wrapper select-none position-absolute w-100 scroll-anim-fade-in">
            <div className="marquee-content text-white">
              <span>Smart &nbsp; Factory &nbsp; Industries &nbsp; &amp; &nbsp;</span>
              <span>IIoT Enterprise Architectures </span>
            </div>
          </div>

          {/* Main Content Layout */}
          <div className="container position-relative z-3 my-5">
            <div className="row align-items-center py-4">

              {/* Left Side Info */}
              <div className="col-12 col-md-5 text-start d-flex flex-column align-items-start hero-left-pane text-white mb-4 mb-md-0 scroll-anim-fade-in-left">
                <div className="asterisk-icon mb-3 text-amber fs-3" style={{ color: '#d97706' }}>✳</div>
                <h1 className="fw-bold mb-3 display-5">Smart Manufacturing</h1>
                <p className="tagline mb-4 text-white" style={{ fontSize: '1rem', lineHeight: '1.6', opacity: 0.9 }}>
                  At Affsoft IT Solutions Pvt. Ltd., we eliminate the data siloes and technical lag that cause industrial plant downtime. Our manufacturing automation practice pairs modern IIoT sensor integration with robust MES execution architectures and predictive analytics to convert physical factory lines into high-efficiency strategic spaces.
                </p>
                <button className="btn btn-outline-light d-flex align-items-center gap-2 px-4 py-2 rounded-pill fw-semibold">
                  <span>➔</span>
                  <span>Propose a Smart Factory Plan</span>
                </button>
              </div>

              {/* Center Section */}
              <div className="col-12 col-md-4 text-center mb-4 mb-md-0 hero-center-pane scroll-anim-flip">
                <div className="typing-badge px-4 py-3 mx-auto bg-dark bg-opacity-75 rounded-pill border border-secondary shadow-lg d-inline-block">
                  <span className="typed-text text-white fw-mono fs-4">{displayedText}</span>
                  <span className="cursor fs-4 animate-blink" style={{ color: '#d97706' }}>|</span>
                </div>
              </div>

              {/* Right Section */}
              <div className="col-12 col-md-3 d-flex justify-content-center justify-content-md-end hero-right-pane scroll-anim-fade-in-right">
                <div className="glass-card-frame shadow-lg p-3 bg-white bg-opacity-10 rounded-4 border border-light" style={{ width: '220px', backdropFilter: 'blur(10px)' }}>
                  <div className={`inner-graphic-card graphic-${cardData[cardIndex].type} d-flex align-items-center justify-content-center rounded-3 bg-dark`} style={{ height: '180px' }}>
                    <span className="graphic-icon display-3">{cardData[cardIndex].content}</span>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* ================= CORE SOLUTIONS SECTION ================= */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
              <span className="badge px-3 py-2 rounded-pill mb-2 text-uppercase text-white" style={{ backgroundColor: '#d97706' }}>Operational Technology Optimization</span>
              <h2 className="fw-bold text-dark display-6">Deploy High-Throughput Telemetry Links & Fully Integrated Shop Floor Control</h2>
              <p className="text-muted mt-3">
                Isolated machine lines, fragile manual shift handovers, and reactive maintenance loops impact factory output. At Affsoft IT Solutions Pvt. Ltd., we build secure, zero-downtime, and systematically tracked smart factory architectures.
              </p>
            </div>

            <div className="row g-4">
              {coreServices.map((service, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="card h-100 shadow-sm border-0 rounded-4 p-4 hover-translate transition-all">
                    <div className="fs-1 mb-3" style={{ color: '#d97706' }}>{service.icon}</div>
                    <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                    <p className="text-muted mb-0 small">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Business Value Checklist */}
            <div className="mt-5 p-4 rounded-4 bg-light border scroll-anim-fade-in-up">
              <h5 className="fw-bold text-center text-dark mb-4">Core Operational Gains of Strategic Enterprise Industry 4.0 Transformation</h5>
              <div className="row g-3">
                {designBenefits.map((benefit, index) => (
                  <div className="col-6 col-md-3 d-flex align-items-center gap-2" key={index}>
                    <span className="text-success fs-5">✓</span>
                    <span className="text-secondary small fw-medium">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ================= ADVANCED SOLUTIONS OPTIONS ================= */}
        <section className="py-5 bg-light">
          <div className="container">
            <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
              <span className="badge px-3 py-2 rounded-pill mb-2 text-uppercase text-white" style={{ backgroundColor: '#d97706' }}>Distributed Industrial Nodes</span>
              <h2 className="fw-bold text-dark display-6">Adaptive Automation Capabilities Built for High-Density Operations</h2>
              <p className="text-muted mt-3">
                We scale basic plant tracking systems into fully integrated, adaptive digital environments. From deploying computer vision quality inspection checkpoints to managing complex batch processing logic, our blueprints protect your continuous production stability.
              </p>
            </div>

            <div className="row g-4">
              {advancedServices.map((service, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="card h-100 shadow-sm border-0 rounded-4 p-4 bg-white hover-translate transition-all">
                    <div className="fs-1 mb-3" style={{ color: '#d97706' }}>{service.icon}</div>
                    <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                    <p className="text-muted mb-0 small">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= TECHNOLOGIES SECTION ================= */}
        <section className="py-5 bg-dark text-white">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <span className="badge px-3 py-2 rounded-pill mb-2 text-uppercase fw-bold text-dark" style={{ backgroundColor: '#d97706' }}>Smart Factory Matrix</span>
              <h2 className="fw-bold display-6">Industrial Platforms, Frameworks & Connectivity Frameworks We Standardize</h2>
              <p className="text-light-50">We configure, monitor, and safeguard distributed plant automation matrices utilizing strict validation rules.</p>
            </div>

            <div className="row g-4 justify-content-center">
              {Object.entries(technologies).map(([category, list], index) => (
                <div className="col-12 col-sm-6 col-md-4 col-lg scroll-anim-fade-in" key={index}>
                  <div className="bg-secondary bg-opacity-25 p-4 rounded-4 h-100 border border-secondary border-opacity-50">
                    <h5 className="fw-bold mb-3 border-bottom border-secondary pb-2" style={{ color: '#d97706' }}>{category}</h5>
                    <ul className="list-unstyled mb-0">
                      {list.map((tech, i) => (
                        <li key={i} className="mb-2 d-flex align-items-center gap-2 small">
                          <span className="opacity-75" style={{ color: '#d97706' }}>•</span> {tech}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= ROADMAP PROCESS ================= */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-secondary px-3 py-2 rounded-pill mb-2 text-uppercase">Deployment Lifecycle</span>
              <h2 className="fw-bold text-dark display-6">Our Engineered Smart Factory Deployment Blueprint</h2>
              <p className="text-muted">A structured, strictly governed lifecycle designed to safely transition asset automation from isolated lines onto live operational enterprise structures.</p>
            </div>

            <div className="row g-3">
              {processSteps.map((step, index) => (
                <div className="col-12 col-md-6 col-lg scroll-anim-fade-in-up" key={index} style={{ animationDelay: `${index * 0.15}s` }}>
                  <div className="p-4 rounded-4 bg-light border-start border-4 h-100 shadow-sm" style={{ borderLeftColor: '#d97706' }}>
                    <div className="fw-bold display-6 mb-2" style={{ color: '#d97706' }}>{step.num}</div>
                    <h5 className="fw-bold text-dark fs-6">{step.title}</h5>
                    <p className="text-muted small mb-0">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= INDUSTRIES SERVED ================= */}
        <section className="py-5 bg-light">
          <div className="container text-center">
            <h2 className="fw-bold text-dark mb-4 display-6 scroll-anim-fade-in-up">Ecolored Manufacturing Spaces We Transform</h2>
            <div className="d-flex flex-wrap justify-content-center gap-2 max-width-900 mx-auto scroll-anim-zoom-in">
              {industries.map((industry, index) => (
                <span className="badge bg-white text-dark border px-3 py-2 rounded-pill shadow-sm fs-7 fw-medium" key={index}>
                  ⚙️ {industry}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ================= WHY CHOOSE AFFSOFT ================= */}
        <section className="py-5 bg-white border-top">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <h2 className="fw-bold text-dark display-6">Why Partner with Affsoft IT Solutions Pvt. Ltd.?</h2>
              <p className="text-muted">We combine physical systems instrumentation expertise with modern data engineering to guarantee highly predictable operational behavior.</p>
            </div>

            <div className="row g-3">
              {whyChooseUs.map((reason, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-fade-in-left" key={index} style={{ animationDelay: `${(index % 3) * 0.1}s` }}>
                  <div className="d-flex align-items-center gap-3 p-3 bg-light rounded-3 h-100">
                    <span className="fs-4" style={{ color: '#d97706' }}>🛡️</span>
                    <span className="text-dark fw-semibold small">{reason}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Custom Styles Injection */}
        <style>{`
        .hover-translate {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .hover-translate:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
        }
        .text-light-50 {
          color: rgba(255, 255, 255, 0.7);
        }
        .max-width-700 {
          max-width: 700px;
        }
        .max-width-900 {
          max-width: 900px;
        }
        .animate-blink {
          animation: blink 1s step-end infinite;
        }
        @keyframes blink {
          from, to { color: transparent }
          50% { color: inherit }
        }

        /* --- Scroll Animation Core --- */
        .scroll-anim-fade-in,
        .scroll-anim-fade-in-up,
        .scroll-anim-fade-in-left,
        .scroll-anim-fade-in-right,
        .scroll-anim-flip,
        .scroll-anim-zoom-in {
          opacity: 0;
          will-change: transform, opacity;
          transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }

        /* Visibility Trigger via IntersectionObserver */
        .animate-visible {
          opacity: 1 !important;
          transform: none !important;
        }

        /* --- Animations Configurations --- */
        .scroll-anim-fade-in-up {
          transform: translateY(40px);
        }
        .scroll-anim-fade-in-left {
          transform: translateX(-50px);
        }
        .scroll-anim-fade-in-right {
          transform: translateX(50px);
        }
        .scroll-anim-zoom-in {
          transform: scale(0.85);
        }
        .scroll-anim-flip {
          transform: perspective(400px) rotateY(90deg);
          transition: all 1s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }
        
        .scroll-anim-flip.animate-visible {
          transform: perspective(400px) rotateY(0deg) !important;
        }
      `}</style>

      </div>
    </>
  );
};

export default ManufacturingSolutions;